import { useHasPermission } from 'mf_core/auth';
import { ObjectMap } from 'mf_core/query';

export function isEmpty(value: any): boolean {
  if (!value || value === null || value === undefined) return true;
  if (typeof value === 'string') {
    if (value?.trim() === '') return true;
  }
  if (typeof value === 'object') {
    if (!Object.keys(value) || Object.keys(value)?.length === 0) return true;
    if (Object.keys(value)?.length === 1 && Object.keys(value)?.includes('')) {
      return true;
    }
  }
  return false;
}

export const userRoles: ObjectMap = {
  ADMIN: 'Admin',
  BASIC: 'Basic',
  PURCHASE: 'Purchase',
  SUPPORT: 'Support',
};

export const GetUserRole = () => {
  const hasPermission = useHasPermission();
  if (hasPermission(['ResellerAdmin'])) return userRoles.ADMIN;
  else if (
    (hasPermission(['SeeResellerPriceAndStockOnCatalogue']) ||
      hasPermission(['CreateBaskets'])) &&
    (hasPermission(['PlaceOrders']) ||
      hasPermission(['FulfillmentOrdering']) ||
      hasPermission(['ViewOrderStatus']))
  )
    return userRoles.PURCHASE;
  else if (
    hasPermission(['ViewAccountStatements']) ||
    hasPermission(['ViewOrderStatus']) ||
    hasPermission(['CreateReturns'])
  )
    return userRoles.SUPPORT;
  else return userRoles.BASIC;
};

export function getAmount(value?: number | string) {
  if (!value || isEmpty(value)) return 0;
  if (typeof value === 'string') return +value?.replace(/\s+/g, '');
  return value;
}

export function percentage(value: number, total: number) {
  if (value === 0 || total === 0) return 0;
  return getAmount(((value / total) * 100).toFixed(2));
}

export function capitalizeFirstLetter(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function capitalizeSentence(sentence: string | undefined): string {
  if (!sentence) return '';
  const words = sentence.split(' ');
  const capitalizedWords = words.map((word) => capitalizeFirstLetter(word));
  const capitalizedSentence = capitalizedWords.join(' ');
  return capitalizedSentence;
}

export function getSKUsMap(skus: any = []) {
  const skuMap: ObjectMap = {};

  skus.forEach((sku: ObjectMap) => {
    skuMap[sku?.partNumber ?? sku?.skuNumber] = sku;
  });

  return skuMap;
}

export function getSKUMap(skus: any = []) {
  const skuMap: ObjectMap = {};

  skus.forEach((sku: ObjectMap) => {
    skuMap[sku?.partnumber ?? sku?.skuNumber] = sku;
  });

  return skuMap;
}

export function sortJSONListByKey(jsonList: Array<ObjectMap>, key: string) {
  jsonList?.sort((productDetailsA: ObjectMap, productDetailsB: ObjectMap) => {
    if (productDetailsA[key] > productDetailsB[key]) return 1;
    else if (productDetailsA[key] < productDetailsB[key]) return -1;
    else return 0;
  });

  return jsonList;
}

export function sortJSONByNullValue(jsonList: Array<ObjectMap>, key: string) {
  jsonList.sort((productA: ObjectMap, productB: ObjectMap) => {
    if (JSON.stringify(productA?.[key]) === '{}') {
      return 1;
    }

    if (JSON.stringify(productB?.[key]) === '{}') {
      return -1;
    }

    if (productA?.[key] === productB?.[key]) {
      return 0;
    }

    return productA?.[key] < productB?.[key] ? -1 : 1;
  });
  return jsonList;
}

export function sortJSONListByBooleanKey(
  jsonList: Array<ObjectMap>,
  key: string,
  orderBy: string
) {
  jsonList?.sort((productA: ObjectMap, productB: ObjectMap) => {
    if (orderBy === 'desc') {
      return Number(productB?.[key]) - Number(productA?.[key]);
    }

    return Number(productA?.[key]) - Number(productB?.[key]);
  });
  return jsonList;
}

export function sortJSONListByTwoBooleanKey(
  jsonList: Array<ObjectMap>,
  key1: string,
  key2: string,
  orderBy: string
) {
  sortJSONListByBooleanKey(jsonList, key1, orderBy).sort(
    (productA, productB) => {
      if (orderBy === 'desc') {
        return Number(productB[key2]) - Number(productA[key2]);
      }

      return Number(productA[key2]) - Number(productB[key2]);
    }
  );

  return jsonList;
}

export function getFormattedNumber(
  num: number,
  currencySymbol = '$',
  zeroRequired = false
): string {
  num = num * 1;

  if (num >= 1000000000) {
    return `${currencySymbol}${(num / 1000000000)
      .toFixed(1)
      .replace(/\.0$/, '')}B`;
  }
  if (num >= 1000000) {
    return `${currencySymbol}${(num / 1000000)
      .toFixed(1)
      .replace(/\.0$/, '')}M`;
  }
  if (num >= 1000) {
    return `${currencySymbol}${(num / 1000)?.toFixed(1)?.replace(/\.0$/, '')}K`;
  }
  if (num === 0) {
    return zeroRequired ? `${currencySymbol}0` : '';
  }
  return `${currencySymbol}${num?.toFixed(1)?.replace(/\.0$/, '')}`;
}
